/**
 * FluxNodeDashboard Component
 *
 * Main dashboard displaying all FluxNodes with comprehensive statistics,
 * rewards tracking, network information, real APY, and parallel assets.
 */

import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { FluxNode } from '../../types/fluxNode.types';
import { TIER_REQUIREMENTS } from '../../types/fluxNode.types';
import { FluxNodeCard } from './FluxNodeCard';
import { Icons } from '../Icons';
import type { ExtendedNetworkStats, ParallelAssetsSummary, RewardPayout } from '../../services/fluxNodeApi';
import { fluxNodeAPI } from '../../services/fluxNodeApi';
import type { WalletBalanceData } from '../../hooks/useFluxNodes';
import type { NodePurchaseTracking, NodeAliases } from '../../pages/FluxNodeMonitor';
import type { DatavexExpirationMap } from '../../services/datavexApi';
import { RewardsCumulativeChart } from './RewardsCumulativeChart';
import { HostingPromoPopup } from './HostingPromoPopup';
import { getNetworkStats7DayVariation, useDatavexHostingPrices, useDatavexHostedIps } from '../../hooks/useFluxNodes';

// Helper to safely format numbers - handles undefined, null, NaN, objects
const safeNum = (value: unknown): number => {
  if (typeof value === 'number' && !isNaN(value) && isFinite(value)) {
    return value;
  }
  return 0;
};

interface FluxNodeDashboardProps {
  nodes: FluxNode[];
  fluxPrice: number;
  isLoading?: boolean;
  onRefresh?: () => void;
  // Wallet addresses for hosting quote
  wallets?: string[];
  // New props for real APY and parallel assets
  extendedStats?: ExtendedNetworkStats;
  parallelAssets?: ParallelAssetsSummary[];
  rewardPayouts?: RewardPayout[];
  realRewards?: {
    daily: number;
    monthly: number;
    yearly: number;
    monthlyUSD: number;
    yearlyUSD: number;
    avgROI: number;
    byTier: {
      CUMULUS: { apy: number; daily: number; monthly: number };
      NIMBUS: { apy: number; daily: number; monthly: number };
      STRATUS: { apy: number; daily: number; monthly: number };
    };
  };
  // Node purchase price tracking for gain/loss calculation
  nodePurchaseTracking?: NodePurchaseTracking;
  onUpdateNodePurchasePrice?: (nodeId: string, purchasePrice: number, purchaseDate?: string) => void;
  onUpdateTrackingStartDate?: (nodeId: string, startDate: string | null) => void;
  onRemoveNodeTracking?: (nodeId: string) => void;
  // Wallet balances for rewards calculation
  walletBalances?: WalletBalanceData[];
  // Node aliases for custom naming
  nodeAliases?: NodeAliases;
  onUpdateNodeAlias?: (nodeId: string, alias: string) => void;
  onRemoveNodeAlias?: (nodeId: string) => void;
  // Datavex expiration info for nodes
  datavexExpirations?: DatavexExpirationMap | null;
}

export function FluxNodeDashboard({
  nodes,
  fluxPrice,
  isLoading,
  onRefresh,
  wallets = [],
  extendedStats,
  parallelAssets,
  rewardPayouts,
  realRewards,
  nodePurchaseTracking,
  onUpdateNodePurchasePrice,
  onUpdateTrackingStartDate,
  onRemoveNodeTracking,
  walletBalances,
  nodeAliases,
  onUpdateNodeAlias,
  onRemoveNodeAlias,
  datavexExpirations
}: FluxNodeDashboardProps) {
  const { t } = useTranslation();
  const [filterTier, setFilterTier] = useState<'ALL' | 'CUMULUS' | 'NIMBUS' | 'STRATUS'>('ALL');
  const [filterStatus, setFilterStatus] = useState<'ALL' | 'CONFIRMED' | 'STARTED'>('ALL');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [searchQuery, setSearchQuery] = useState('');
  // Track which node is expanded in list view (accordion behavior - only one at a time)
  const [expandedListNodeId, setExpandedListNodeId] = useState<string | null>(null);
  // Advanced filters
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [filterMaintenance, setFilterMaintenance] = useState<'ALL' | 'OPEN' | 'CLOSED'>('ALL');
  const [filterPnr, setFilterPnr] = useState<'ALL' | 'ELIGIBLE' | 'NOT_ELIGIBLE'>('ALL');
  const [filterRankMin, setFilterRankMin] = useState('');
  const [filterRankMax, setFilterRankMax] = useState('');
  const [filterUptimeMin, setFilterUptimeMin] = useState('');
  const [filterNextPaymentMax, setFilterNextPaymentMax] = useState('');
  // Sort
  type SortField = 'default' | 'rank' | 'uptime' | 'nextPayment' | 'maintenance' | 'pnr';
  const [sortField, setSortField] = useState<SortField>('default');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Fetch hosting prices from Datavex
  const { data: datavexStats } = useDatavexHostingPrices();
  // Fetch list of IPs hosted by Datavex for badge display
  const { data: datavexHostedIps } = useDatavexHostedIps();

  // Helper: compute uptime days from activesince
  const getUptimeDays = (node: FluxNode): number => {
    if (!node.activesince) return 0;
    const activeSinceMs = typeof node.activesince === 'string'
      ? (Number(node.activesince) > 1e12 ? Number(node.activesince) : Number(node.activesince) * 1000)
      : 0;
    if (!activeSinceMs || isNaN(activeSinceMs)) return 0;
    return Math.max(0, (Date.now() - activeSinceMs) / (1000 * 60 * 60 * 24));
  };

  // Helper: compute PNR estimate from activesince (30 days = eligible)
  const getPnrPercent = (node: FluxNode): number => {
    const days = getUptimeDays(node);
    return Math.min(100, Math.round((days / 30) * 100));
  };

  // Helper: next reward in minutes
  const getNextRewardMinutes = (node: FluxNode): number => node.rank / 2;

  // Helper: maintenance window
  const getMaintenanceWindow = (node: FluxNode) => {
    if (!extendedStats?.blockHeight) return null;
    return fluxNodeAPI.calculateMaintenanceWindow(node.last_confirmed_height, extendedStats.blockHeight);
  };

  // Check if any advanced filter is active
  const hasActiveAdvancedFilters = filterMaintenance !== 'ALL' || filterPnr !== 'ALL' ||
    filterRankMin !== '' || filterRankMax !== '' || filterUptimeMin !== '' ||
    filterNextPaymentMax !== '' || sortField !== 'default';

  // Filter nodes - must be before early returns to respect Rules of Hooks
  const filteredNodes = useMemo(() => {
    if (!nodes || nodes.length === 0) return [];
    let result = nodes.filter(node => {
      if (filterTier !== 'ALL' && node.tier !== filterTier) return false;
      if (filterStatus !== 'ALL') {
        const status = (node.confirmed_height || 0) > 0 ? 'CONFIRMED' : 'STARTED';
        if (status !== filterStatus) return false;
      }
      // Search by IP or alias
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase().trim();
        const nodeId = `${node.txhash}:${node.outidx}`;
        const alias = nodeAliases?.[nodeId]?.toLowerCase() || '';
        const ip = node.ip?.toLowerCase() || '';
        if (!ip.includes(query) && !alias.includes(query)) return false;
      }
      // Maintenance window filter
      if (filterMaintenance !== 'ALL') {
        const mw = getMaintenanceWindow(node);
        if (mw) {
          if (filterMaintenance === 'OPEN' && mw.status !== 'open') return false;
          if (filterMaintenance === 'CLOSED' && mw.status !== 'closed') return false;
        }
      }
      // PNR filter (estimated from activesince)
      if (filterPnr !== 'ALL') {
        const pnrPct = getPnrPercent(node);
        if (filterPnr === 'ELIGIBLE' && pnrPct < 100) return false;
        if (filterPnr === 'NOT_ELIGIBLE' && pnrPct >= 100) return false;
      }
      // Rank range filter
      if (filterRankMin !== '') {
        const min = parseInt(filterRankMin, 10);
        if (!isNaN(min) && node.rank < min) return false;
      }
      if (filterRankMax !== '') {
        const max = parseInt(filterRankMax, 10);
        if (!isNaN(max) && node.rank > max) return false;
      }
      // Uptime min filter (days)
      if (filterUptimeMin !== '') {
        const minDays = parseFloat(filterUptimeMin);
        if (!isNaN(minDays) && getUptimeDays(node) < minDays) return false;
      }
      // Next payment max filter (hours)
      if (filterNextPaymentMax !== '') {
        const maxHours = parseFloat(filterNextPaymentMax);
        if (!isNaN(maxHours) && getNextRewardMinutes(node) > maxHours * 60) return false;
      }
      return true;
    });

    // Sort
    if (sortField !== 'default') {
      result = [...result].sort((a, b) => {
        let valA = 0, valB = 0;
        switch (sortField) {
          case 'rank':
            valA = a.rank; valB = b.rank; break;
          case 'uptime':
            valA = getUptimeDays(a); valB = getUptimeDays(b); break;
          case 'nextPayment':
            valA = getNextRewardMinutes(a); valB = getNextRewardMinutes(b); break;
          case 'maintenance': {
            const mwA = getMaintenanceWindow(a);
            const mwB = getMaintenanceWindow(b);
            valA = mwA ? mwA.minutesRemaining : -1;
            valB = mwB ? mwB.minutesRemaining : -1;
            break;
          }
          case 'pnr':
            valA = getPnrPercent(a); valB = getPnrPercent(b); break;
        }
        return sortDirection === 'asc' ? valA - valB : valB - valA;
      });
    }

    return result;
  }, [nodes, filterTier, filterStatus, searchQuery, nodeAliases, filterMaintenance, filterPnr,
      filterRankMin, filterRankMax, filterUptimeMin, filterNextPaymentMax, sortField, sortDirection, extendedStats?.blockHeight]);

  // Reset advanced filters
  const resetAdvancedFilters = () => {
    setFilterMaintenance('ALL');
    setFilterPnr('ALL');
    setFilterRankMin('');
    setFilterRankMax('');
    setFilterUptimeMin('');
    setFilterNextPaymentMax('');
    setSortField('default');
    setSortDirection('asc');
  };

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 20px' }}>
        <Icons.Activity size={48} style={{ opacity: 0.3, margin: '0 auto 16px', animation: 'spin 2s linear infinite' }} />
        <p style={{ color: 'var(--muted)' }}>{t('fluxNode.dashboard.loading')}</p>
      </div>
    );
  }

  if (!nodes || nodes.length === 0) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: '60px 20px' }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔌</div>
        <h3 style={{ marginBottom: '8px' }}>{t('fluxNode.dashboard.noNodesFound')}</h3>
        <p style={{ color: 'var(--muted)', marginBottom: '20px' }}>
          {t('fluxNode.dashboard.addWalletToMonitor')}
        </p>
      </div>
    );
  }

  // Calculate aggregated statistics
  // All nodes returned by the API are active (API only returns nodes from deterministic list)
  const activeNodes = nodes.filter(n => (n.confirmed_height || 0) > 0).length;
  const startedNodes = nodes.length - activeNodes; // Nodes not yet confirmed

  const nodesByTier = {
    CUMULUS: nodes.filter(n => n.tier === 'CUMULUS').length,
    NIMBUS: nodes.filter(n => n.tier === 'NIMBUS').length,
    STRATUS: nodes.filter(n => n.tier === 'STRATUS').length
  };

  // Calculate total collateral and value (collateral is determined by tier)
  const totalCollateral = nodes.reduce((sum, node) => {
    return sum + (TIER_REQUIREMENTS[node.tier]?.collateral || 0);
  }, 0);

  const totalValue = totalCollateral * fluxPrice;

  // Use real rewards if available, otherwise fallback to estimated
  const totalMonthlyRewards = realRewards?.monthly ?? nodes.reduce((sum, node) => {
    const rewardsPerBlock: Record<string, number> = {
      CUMULUS: 7.5,
      NIMBUS: 31.25,
      STRATUS: 93.75
    };
    const rewardPerBlock = rewardsPerBlock[node.tier] || 0;
    const dailyReward = (rewardPerBlock * 2880) / (2880 * 2);
    return sum + (dailyReward * 30);
  }, 0);

  const totalYearlyRewards = realRewards?.yearly ?? totalMonthlyRewards * 12;
  const avgROI = realRewards?.avgROI ?? (totalValue > 0 ? ((totalYearlyRewards * fluxPrice) / totalValue) * 100 : 0);

  // Calculate parallel assets totals
  const totalClaimable = parallelAssets?.reduce((sum, pa) => sum + pa.totalClaimable, 0) ?? 0;
  const totalClaimed = parallelAssets?.reduce((sum, pa) => sum + pa.totalClaimed, 0) ?? 0;

  // Calculate recent payouts
  const last7DaysPayouts = rewardPayouts?.filter(p => p.timestamp >= Date.now() - 7 * 24 * 60 * 60 * 1000) ?? [];
  const last7DaysTotal = last7DaysPayouts.reduce((sum, p) => sum + p.amount, 0);

  // Calculate accumulated rewards: Total wallet balance - locked collateral = rewards received
  const totalWalletBalance = walletBalances?.reduce((sum, wb) => sum + wb.balance, 0) ?? 0;
  const accumulatedRewards = totalWalletBalance - totalCollateral;

  // Calculate collateral gain/loss from per-node tracking
  const hasTracking = nodePurchaseTracking && Object.keys(nodePurchaseTracking).length > 0;
  let collateralGainLoss = {
    hasData: false,
    totalInvested: 0,
    currentValue: totalValue,
    gainLossUSD: 0,
    gainLossPercent: 0,
    avgPurchasePrice: 0,
    nodesTracked: 0
  };

  if (hasTracking && fluxPrice > 0 && nodes.length > 0) {
    let totalInvestedUSD = 0;
    let totalTrackedCollateral = 0;
    let nodesTracked = 0;

    for (const node of nodes) {
      const nodeId = `${node.txhash}:${node.outidx}`;
      const tracking = nodePurchaseTracking[nodeId];
      if (tracking && tracking.purchasePrice > 0) {
        const nodeCollateral = TIER_REQUIREMENTS[node.tier]?.collateral || 0;
        totalInvestedUSD += nodeCollateral * tracking.purchasePrice;
        totalTrackedCollateral += nodeCollateral;
        nodesTracked++;
      }
    }

    if (nodesTracked > 0 && totalInvestedUSD > 0) {
      const currentTrackedValue = totalTrackedCollateral * fluxPrice;
      const gainLossUSD = currentTrackedValue - totalInvestedUSD;
      const gainLossPercent = (gainLossUSD / totalInvestedUSD) * 100;

      collateralGainLoss = {
        hasData: true,
        totalInvested: totalInvestedUSD,
        currentValue: currentTrackedValue,
        gainLossUSD,
        gainLossPercent,
        avgPurchasePrice: totalInvestedUSD / totalTrackedCollateral,
        nodesTracked
      };
    }
  }

  return (
    <div>
      {/* Header with Refresh */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h2 style={{ fontSize: '24px', fontWeight: 700, marginBottom: '4px' }}>
            {t('fluxNode.dashboard.monitorTitle')}
          </h2>
          <p style={{ fontSize: '14px', color: 'var(--muted)' }}>
            {t('fluxNode.dashboard.monitoringNodes', { count: nodes.length })}
          </p>
        </div>
        {onRefresh && (
          <button
            onClick={onRefresh}
            style={{
              padding: '8px 16px',
              fontSize: '14px',
              fontWeight: 600,
              background: 'var(--accent)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <Icons.RefreshCw size={16} />
            {t('fluxNode.dashboard.refresh')}
          </button>
        )}
      </div>

      {/* Stats Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
        <div className="glass-card" style={{ padding: '16px' }}>
          <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '8px' }}>
            {t('fluxNode.dashboard.totalNodes')}
          </div>
          <div style={{ fontSize: '28px', fontWeight: 700 }}>
            {nodes.length}
          </div>
          <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '4px' }}>
            {activeNodes} {t('fluxNode.dashboard.active', { count: activeNodes })}{startedNodes > 0 ? ` • ${startedNodes} ${t('fluxNode.dashboard.starting')}` : ''}
          </div>
        </div>

        <div className="glass-card" style={{ padding: '16px' }}>
          <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '8px' }}>
            {t('fluxNode.dashboard.totalValue')}
          </div>
          <div style={{ fontSize: '28px', fontWeight: 700 }}>
            ${totalValue.toLocaleString(undefined, { maximumFractionDigits: 0 })}
          </div>
          <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '4px' }}>
            {totalCollateral.toLocaleString()} FLUX
          </div>
        </div>

        <div className="glass-card" style={{ padding: '16px' }}>
          <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '8px' }}>
            {t('fluxNode.dashboard.monthlyRevenue')}
          </div>
          <div style={{ fontSize: '28px', fontWeight: 700, color: '#6366f1' }}>
            {safeNum(totalMonthlyRewards).toFixed(0)} FLUX
          </div>
          <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '4px' }}>
            ${(safeNum(totalMonthlyRewards) * safeNum(fluxPrice)).toLocaleString(undefined, { maximumFractionDigits: 0 })} USD
          </div>
        </div>

        <div className="glass-card" style={{ padding: '16px' }}>
          <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '8px' }}>
            {t('fluxNode.dashboard.averageROI')}
          </div>
          <div style={{ fontSize: '28px', fontWeight: 700, color: '#10b981' }}>
            {safeNum(avgROI).toFixed(1)}%
          </div>
          <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '4px' }}>
            {t('fluxNode.dashboard.perYear')}
          </div>
        </div>
      </div>

      {/* Collateral Investment Tracking */}
      {collateralGainLoss.hasData && (
        <div
          className="glass-card"
          style={{
            padding: '20px',
            marginBottom: '24px',
            background: collateralGainLoss.gainLossUSD >= 0
              ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.1), rgba(16, 185, 129, 0.05))'
              : 'linear-gradient(135deg, rgba(239, 68, 68, 0.1), rgba(239, 68, 68, 0.05))',
            border: `1px solid ${collateralGainLoss.gainLossUSD >= 0 ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`
          }}
        >
          <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Icons.TrendingUp size={18} />
            {t('fluxNode.dashboard.collateralPerformance')}
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px' }}>
            <div style={{ textAlign: 'center', padding: '12px', background: 'var(--bg-secondary)', borderRadius: '12px' }}>
              <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '4px' }}>
                {t('fluxNode.dashboard.totalInvested')}
              </div>
              <div style={{ fontSize: '20px', fontWeight: 700 }}>
                ${collateralGainLoss.totalInvested.toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </div>
              <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '2px' }}>
                @ ${collateralGainLoss.avgPurchasePrice.toFixed(4)}/FLUX avg
              </div>
            </div>
            <div style={{ textAlign: 'center', padding: '12px', background: 'var(--bg-secondary)', borderRadius: '12px' }}>
              <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '4px' }}>
                {t('fluxNode.dashboard.currentValue')}
              </div>
              <div style={{ fontSize: '20px', fontWeight: 700 }}>
                ${collateralGainLoss.currentValue.toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </div>
              <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '2px' }}>
                @ ${fluxPrice.toFixed(4)}/FLUX
              </div>
            </div>
            <div style={{ textAlign: 'center', padding: '12px', background: 'var(--bg-secondary)', borderRadius: '12px' }}>
              <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '4px' }}>
                {t('fluxNode.dashboard.gainLoss')}
              </div>
              <div style={{
                fontSize: '20px',
                fontWeight: 700,
                color: collateralGainLoss.gainLossUSD >= 0 ? '#10b981' : '#ef4444'
              }}>
                {collateralGainLoss.gainLossUSD >= 0 ? '+' : ''}${collateralGainLoss.gainLossUSD.toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </div>
              <div style={{
                fontSize: '11px',
                color: collateralGainLoss.gainLossPercent >= 0 ? '#10b981' : '#ef4444',
                marginTop: '2px'
              }}>
                {collateralGainLoss.gainLossPercent >= 0 ? '+' : ''}{collateralGainLoss.gainLossPercent.toFixed(1)}%
              </div>
            </div>
            <div style={{ textAlign: 'center', padding: '12px', background: 'var(--bg-secondary)', borderRadius: '12px' }}>
              <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '4px' }}>
                {t('fluxNode.dashboard.nodesTracked')}
              </div>
              <div style={{ fontSize: '20px', fontWeight: 700 }}>
                {collateralGainLoss.nodesTracked}/{nodes.length}
              </div>
              <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '2px' }}>
                {t('fluxNode.dashboard.nodes')}
              </div>
            </div>
          </div>
          <div style={{ marginTop: '12px', fontSize: '11px', color: 'var(--muted)', textAlign: 'center' }}>
            💡 {t('fluxNode.monitoring.clickNodeToEnterPrice')}
          </div>
        </div>
      )}

      {/* Tier Distribution with Real APY */}
      <div className="glass-card" style={{ padding: '20px', marginBottom: '24px' }}>
        <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>
          {t('fluxNode.dashboard.distributionByTier')}
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
          <div style={{ textAlign: 'center', padding: '12px', background: 'rgba(59, 130, 246, 0.1)', borderRadius: '12px' }}>
            <div style={{ fontSize: '32px', fontWeight: 700, color: '#3b82f6' }}>
              {nodesByTier.CUMULUS}
            </div>
            <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '8px' }}>
              CUMULUS
            </div>
            {extendedStats && (
              <div style={{ fontSize: '14px', fontWeight: 600, color: '#10b981' }}>
                APY: {safeNum(extendedStats.cumulusAPY).toFixed(1)}%
              </div>
            )}
            {realRewards?.byTier.CUMULUS && (
              <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '4px' }}>
                ~{safeNum(realRewards.byTier.CUMULUS.daily).toFixed(2)} {t('fluxNode.dashboard.fluxPerDayPerNode')}
              </div>
            )}
          </div>
          <div style={{ textAlign: 'center', padding: '12px', background: 'rgba(139, 92, 246, 0.1)', borderRadius: '12px' }}>
            <div style={{ fontSize: '32px', fontWeight: 700, color: '#8b5cf6' }}>
              {nodesByTier.NIMBUS}
            </div>
            <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '8px' }}>
              NIMBUS
            </div>
            {extendedStats && (
              <div style={{ fontSize: '14px', fontWeight: 600, color: '#10b981' }}>
                APY: {safeNum(extendedStats.nimbusAPY).toFixed(1)}%
              </div>
            )}
            {realRewards?.byTier.NIMBUS && (
              <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '4px' }}>
                ~{safeNum(realRewards.byTier.NIMBUS.daily).toFixed(2)} {t('fluxNode.dashboard.fluxPerDayPerNode')}
              </div>
            )}
          </div>
          <div style={{ textAlign: 'center', padding: '12px', background: 'rgba(236, 72, 153, 0.1)', borderRadius: '12px' }}>
            <div style={{ fontSize: '32px', fontWeight: 700, color: '#ec4899' }}>
              {nodesByTier.STRATUS}
            </div>
            <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '8px' }}>
              STRATUS
            </div>
            {extendedStats && (
              <div style={{ fontSize: '14px', fontWeight: 600, color: '#10b981' }}>
                APY: {safeNum(extendedStats.stratusAPY).toFixed(1)}%
              </div>
            )}
            {realRewards?.byTier.STRATUS && (
              <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '4px' }}>
                ~{safeNum(realRewards.byTier.STRATUS.daily).toFixed(2)} {t('fluxNode.dashboard.fluxPerDayPerNode')}
              </div>
            )}
          </div>
        </div>
        {extendedStats && (() => {
          const variation7d = getNetworkStats7DayVariation();
          const formatVar = (v: number | null) => v !== null ? (v >= 0 ? `+${v}` : `${v}`) : '';
          return (
            <div style={{ marginTop: '12px', fontSize: '11px', color: 'var(--muted)', textAlign: 'center', lineHeight: '1.6' }}>
              <span>{t('fluxNode.dashboard.network')}: </span>
              <span style={{ fontWeight: 600, color: 'var(--text)' }}>{extendedStats.totalNodes.toLocaleString()}</span>
              <span> {t('fluxNode.dashboard.nodes')} </span>
              {variation7d?.total !== null && (
                <span style={{ color: (variation7d?.total ?? 0) >= 0 ? '#10b981' : '#ef4444', fontSize: '10px' }}>
                  ({formatVar(variation7d?.total ?? null)})
                </span>
              )}
              <span> • </span>
              <span style={{ color: '#3b82f6', fontWeight: 600 }}>{(extendedStats.cumulusCount || 0).toLocaleString()}</span>
              <span> Cumulus </span>
              {variation7d?.cumulus !== null && (
                <span style={{ color: (variation7d?.cumulus ?? 0) >= 0 ? '#10b981' : '#ef4444', fontSize: '10px' }}>
                  ({formatVar(variation7d?.cumulus ?? null)})
                </span>
              )}
              <span> • </span>
              <span style={{ color: '#8b5cf6', fontWeight: 600 }}>{(extendedStats.nimbusCount || 0).toLocaleString()}</span>
              <span> Nimbus </span>
              {variation7d?.nimbus !== null && (
                <span style={{ color: (variation7d?.nimbus ?? 0) >= 0 ? '#10b981' : '#ef4444', fontSize: '10px' }}>
                  ({formatVar(variation7d?.nimbus ?? null)})
                </span>
              )}
              <span> • </span>
              <span style={{ color: '#ec4899', fontWeight: 600 }}>{(extendedStats.stratusCount || 0).toLocaleString()}</span>
              <span> Stratus </span>
              {variation7d?.stratus !== null && (
                <span style={{ color: (variation7d?.stratus ?? 0) >= 0 ? '#10b981' : '#ef4444', fontSize: '10px' }}>
                  ({formatVar(variation7d?.stratus ?? null)})
                </span>
              )}
              <br />
              <span>{t('fluxNode.dashboard.blockReward')}: {extendedStats.rewardPerBlock} FLUX | {extendedStats.blocksPerDay.toLocaleString()} {t('fluxNode.dashboard.blocksPerDay')}</span>
            </div>
          );
        })()}
      </div>

      {/* Parallel Assets Section */}
      {parallelAssets && parallelAssets.length > 0 && (totalClaimable > 0 || totalClaimed > 0) && (
        <div className="glass-card" style={{ padding: '20px', marginBottom: '24px' }}>
          <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Icons.Layers size={18} />
            {t('fluxNode.dashboard.parallelAssets')}
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px' }}>
            <div style={{ textAlign: 'center', padding: '12px', background: 'rgba(16, 185, 129, 0.1)', borderRadius: '12px' }}>
              <div style={{ fontSize: '24px', fontWeight: 700, color: '#10b981' }}>
                {safeNum(totalClaimable).toFixed(4)}
              </div>
              <div style={{ fontSize: '12px', color: 'var(--muted)' }}>
                {t('fluxNode.dashboard.claimable')}
              </div>
            </div>
            <div style={{ textAlign: 'center', padding: '12px', background: 'rgba(99, 102, 241, 0.1)', borderRadius: '12px' }}>
              <div style={{ fontSize: '24px', fontWeight: 700, color: '#6366f1' }}>
                {safeNum(totalClaimed).toFixed(4)}
              </div>
              <div style={{ fontSize: '12px', color: 'var(--muted)' }}>
                {t('fluxNode.dashboard.alreadyClaimed')}
              </div>
            </div>
          </div>
          {/* Asset breakdown by chain */}
          <div style={{ marginTop: '16px' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {parallelAssets.flatMap(pa => pa.assets).filter(a => a.claimable > 0 || a.claimed > 0).map((asset, idx) => (
                <div
                  key={idx}
                  style={{
                    padding: '6px 12px',
                    background: 'var(--bg-secondary)',
                    borderRadius: '8px',
                    fontSize: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  <span style={{ fontWeight: 600 }}>{asset.chain}</span>
                  <span style={{ color: 'var(--muted)' }}>
                    {asset.claimable > 0 && <span style={{ color: '#10b981' }}>{safeNum(asset.claimable).toFixed(4)} {t('fluxNode.dashboard.claimable').toLowerCase()}</span>}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Recent Payouts Section with Cumulative Chart */}
      {rewardPayouts && rewardPayouts.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '16px', marginBottom: '24px' }}>
          {/* Recent Payouts Stats */}
          <div className="glass-card" style={{ padding: '20px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Icons.DollarSign size={18} />
              {t('fluxNode.dashboard.recentPayouts')}
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px', marginBottom: '16px' }}>
              <div style={{ textAlign: 'center', padding: '12px', background: 'rgba(16, 185, 129, 0.1)', borderRadius: '12px' }}>
                <div style={{ fontSize: '24px', fontWeight: 700, color: '#10b981' }}>
                  {safeNum(last7DaysTotal).toFixed(2)}
                </div>
                <div style={{ fontSize: '12px', color: 'var(--muted)' }}>
                  {t('fluxNode.dashboard.fluxLast7Days')}
                </div>
              </div>
              <div style={{ textAlign: 'center', padding: '12px', background: 'rgba(99, 102, 241, 0.1)', borderRadius: '12px' }}>
                <div style={{ fontSize: '24px', fontWeight: 700, color: '#6366f1' }}>
                  {last7DaysPayouts.length}
                </div>
                <div style={{ fontSize: '12px', color: 'var(--muted)' }}>
                  {t('fluxNode.dashboard.payoutsLast7Days')}
                </div>
              </div>
            </div>

            {/* Accumulated Rewards (Balance - Collateral) + Parallel Assets */}
            {((walletBalances && walletBalances.length > 0 && totalWalletBalance > 0) || totalClaimable > 0) && (
              <div style={{
                padding: '14px',
                background: 'rgba(16, 185, 129, 0.08)',
                borderRadius: '12px',
                marginBottom: '16px',
                border: '1px solid rgba(16, 185, 129, 0.2)'
              }}>
                {/* FLUX Rewards Section */}
                {walletBalances && walletBalances.length > 0 && totalWalletBalance > 0 && (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Icons.Award size={16} style={{ color: accumulatedRewards >= 0 ? '#10b981' : '#ef4444' }} />
                        {t('fluxNode.dashboard.accumulatedRewards', 'Rewards FLUX cumulés')}
                      </div>
                      <div style={{
                        fontSize: '18px',
                        fontWeight: 700,
                        color: accumulatedRewards >= 0 ? '#10b981' : '#ef4444'
                      }}>
                        {accumulatedRewards >= 0 ? '+' : ''}{safeNum(accumulatedRewards).toLocaleString(undefined, { maximumFractionDigits: 2 })} FLUX
                      </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '11px', marginBottom: totalClaimable > 0 ? '12px' : '8px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 8px', background: 'var(--bg-secondary)', borderRadius: '6px' }}>
                        <span style={{ color: 'var(--muted)' }}>{t('fluxNode.dashboard.walletBalance', 'Solde wallet')}</span>
                        <span style={{ fontWeight: 600 }}>{safeNum(totalWalletBalance).toLocaleString(undefined, { maximumFractionDigits: 2 })}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 8px', background: 'var(--bg-secondary)', borderRadius: '6px' }}>
                        <span style={{ color: 'var(--muted)' }}>{t('fluxNode.dashboard.lockedCollateral', 'Collatéral bloqué')}</span>
                        <span style={{ fontWeight: 600 }}>{totalCollateral.toLocaleString()}</span>
                      </div>
                    </div>
                  </>
                )}

                {/* Parallel Assets Claimable Section */}
                {totalClaimable > 0 && (
                  <>
                    {walletBalances && walletBalances.length > 0 && totalWalletBalance > 0 && (
                      <div style={{ borderTop: '1px solid var(--border)', marginBottom: '10px', paddingTop: '10px' }} />
                    )}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Icons.Layers size={16} style={{ color: '#8b5cf6' }} />
                        {t('fluxNode.dashboard.parallelAssetsClaimable', 'Parallel Assets à claim')}
                      </div>
                      <div style={{
                        fontSize: '18px',
                        fontWeight: 700,
                        color: '#8b5cf6'
                      }}>
                        {safeNum(totalClaimable).toFixed(4)} PA
                      </div>
                    </div>
                    {/* Show individual claimable assets */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', fontSize: '11px' }}>
                      {parallelAssets?.flatMap(pa => pa.assets).filter(a => a.claimable > 0).map((asset, idx) => (
                        <div
                          key={idx}
                          style={{
                            padding: '4px 8px',
                            background: 'var(--bg-secondary)',
                            borderRadius: '6px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                        >
                          <span style={{ fontWeight: 600, color: '#8b5cf6' }}>{asset.chain}</span>
                          <span style={{ color: 'var(--muted)' }}>{safeNum(asset.claimable).toFixed(4)}</span>
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {/* Total summary */}
                <div style={{ marginTop: '10px', fontSize: '10px', color: 'var(--muted)', textAlign: 'center' }}>
                  {accumulatedRewards >= 0 && walletBalances && walletBalances.length > 0 && totalWalletBalance > 0
                    ? `≈ $${(accumulatedRewards * fluxPrice).toLocaleString(undefined, { maximumFractionDigits: 0 })} USD ${t('fluxNode.dashboard.inRewards', 'en rewards FLUX')}`
                    : walletBalances && walletBalances.length > 0 && totalWalletBalance > 0
                      ? t('fluxNode.dashboard.negativeBalance', 'Solde inférieur au collatéral')
                      : ''}
                  {totalClaimable > 0 && accumulatedRewards >= 0 && walletBalances && walletBalances.length > 0 && ' + '}
                  {totalClaimable > 0 && `${safeNum(totalClaimable).toFixed(4)} PA ${t('fluxNode.dashboard.pendingClaim', 'en attente de claim')}`}
                </div>
              </div>
            )}
          </div>

          {/* Cumulative Rewards Chart */}
          <div className="glass-card" style={{ padding: '20px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Icons.TrendingUp size={18} />
              {t('fluxNode.dashboard.rewardsEvolution')}
            </h3>
            <RewardsCumulativeChart rewardPayouts={rewardPayouts} />
          </div>
        </div>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        {/* Search by IP or Alias */}
        <div style={{ flex: '1 1 200px', minWidth: '150px', maxWidth: '280px' }}>
          <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--muted)', display: 'block', marginBottom: '6px' }}>
            {t('fluxNode.dashboard.search', 'Search')}
          </label>
          <div style={{ position: 'relative' }}>
            <Icons.Search size={16} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--muted)' }} />
            <input
              type="text"
              placeholder={t('fluxNode.dashboard.searchPlaceholder', 'IP or name...')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px 8px 34px',
                fontSize: '14px',
                background: 'var(--card-bg)',
                color: 'var(--text)',
                border: '1px solid var(--border)',
                borderRadius: '8px',
                outline: 'none'
              }}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                style={{
                  position: 'absolute',
                  right: '8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  color: 'var(--muted)',
                  cursor: 'pointer',
                  padding: '2px',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <Icons.X size={14} />
              </button>
            )}
          </div>
        </div>

        <div>
          <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--muted)', display: 'block', marginBottom: '6px' }}>
            {t('fluxNode.dashboard.filterByTier')}
          </label>
          <select
            aria-label={t('fluxNode.dashboard.filterByTier')}
            value={filterTier}
            onChange={(e) => setFilterTier(e.target.value as any)}
            style={{
              padding: '8px 12px',
              fontSize: '14px',
              fontWeight: 600,
              background: 'var(--card-bg)',
              color: 'var(--text)',
              border: '1px solid var(--border)',
              borderRadius: '8px',
              cursor: 'pointer'
            }}
          >
            <option value="ALL">{t('fluxNode.dashboard.allTiers')}</option>
            <option value="CUMULUS">CUMULUS</option>
            <option value="NIMBUS">NIMBUS</option>
            <option value="STRATUS">STRATUS</option>
          </select>
        </div>

        <div>
          <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--muted)', display: 'block', marginBottom: '6px' }}>
            {t('fluxNode.dashboard.filterByStatus')}
          </label>
          <select
            aria-label={t('fluxNode.dashboard.filterByStatus')}
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as any)}
            style={{
              padding: '8px 12px',
              fontSize: '14px',
              fontWeight: 600,
              background: 'var(--card-bg)',
              color: 'var(--text)',
              border: '1px solid var(--border)',
              borderRadius: '8px',
              cursor: 'pointer'
            }}
          >
            <option value="ALL">{t('fluxNode.dashboard.allStatus')}</option>
            <option value="CONFIRMED">{t('fluxNode.dashboard.activeConfirmed')}</option>
            <option value="STARTED">{t('fluxNode.dashboard.startingNotConfirmed')}</option>
          </select>
        </div>

        {/* Advanced Filters Toggle */}
        <div>
          <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--muted)', display: 'block', marginBottom: '6px' }}>
            &nbsp;
          </label>
          <button
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            style={{
              padding: '8px 12px',
              fontSize: '13px',
              fontWeight: 600,
              background: hasActiveAdvancedFilters ? 'rgba(99, 102, 241, 0.15)' : 'var(--card-bg)',
              color: hasActiveAdvancedFilters ? '#6366f1' : 'var(--text)',
              border: `1px solid ${hasActiveAdvancedFilters ? 'rgba(99, 102, 241, 0.4)' : 'var(--border)'}`,
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'all 0.2s'
            }}
          >
            <Icons.Filter size={14} />
            {t('fluxNode.dashboard.advancedFilters', 'Filters')}
            {hasActiveAdvancedFilters && (
              <span style={{
                width: '6px', height: '6px', borderRadius: '50%',
                background: '#6366f1', display: 'inline-block'
              }} />
            )}
            {showAdvancedFilters ? <Icons.ChevronUp size={14} /> : <Icons.ChevronDown size={14} />}
          </button>
        </div>

        {/* View Mode Toggle */}
        <div style={{ marginLeft: 'auto' }}>
          <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--muted)', display: 'block', marginBottom: '6px' }}>
            {t('fluxNode.dashboard.viewMode', 'View')}
          </label>
          <div style={{ display: 'flex', gap: '4px', background: 'var(--border)', padding: '4px', borderRadius: '8px' }}>
            <button
              onClick={() => setViewMode('grid')}
              style={{
                padding: '6px 12px',
                fontSize: '14px',
                fontWeight: 600,
                background: viewMode === 'grid' ? 'var(--accent)' : 'transparent',
                color: viewMode === 'grid' ? 'white' : 'var(--text)',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'all 0.2s'
              }}
              title={t('fluxNode.dashboard.gridView', 'Grid view')}
            >
              <Icons.Grid size={16} />
            </button>
            <button
              onClick={() => setViewMode('list')}
              style={{
                padding: '6px 12px',
                fontSize: '14px',
                fontWeight: 600,
                background: viewMode === 'list' ? 'var(--accent)' : 'transparent',
                color: viewMode === 'list' ? 'white' : 'var(--text)',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'all 0.2s'
              }}
              title={t('fluxNode.dashboard.listView', 'List view')}
            >
              <Icons.List size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* Advanced Filters Panel */}
      {showAdvancedFilters && (
        <div
          className="glass-card"
          style={{
            padding: '16px 20px',
            marginBottom: '20px',
            border: '1px solid var(--border)',
            borderRadius: '12px'
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h4 style={{ fontSize: '14px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px', margin: 0 }}>
              <Icons.Filter size={16} />
              {t('fluxNode.dashboard.advancedFilters', 'Advanced Filters')}
            </h4>
            {hasActiveAdvancedFilters && (
              <button
                onClick={resetAdvancedFilters}
                style={{
                  padding: '4px 10px',
                  fontSize: '11px',
                  fontWeight: 600,
                  background: 'rgba(239, 68, 68, 0.1)',
                  color: '#ef4444',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                <Icons.X size={12} />
                {t('fluxNode.dashboard.resetFilters', 'Reset')}
              </button>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '12px' }}>
            {/* PNR Filter */}
            <div>
              <label style={{ fontSize: '11px', fontWeight: 600, color: 'var(--muted)', display: 'block', marginBottom: '4px' }}>
                <Icons.Award size={12} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                PNR
              </label>
              <select
                aria-label="PNR"
                value={filterPnr}
                onChange={(e) => setFilterPnr(e.target.value as any)}
                style={{
                  width: '100%',
                  padding: '6px 10px',
                  fontSize: '13px',
                  fontWeight: 600,
                  background: 'var(--card-bg)',
                  color: 'var(--text)',
                  border: `1px solid ${filterPnr !== 'ALL' ? 'rgba(99, 102, 241, 0.4)' : 'var(--border)'}`,
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                <option value="ALL">{t('fluxNode.dashboard.allPnr', 'All')}</option>
                <option value="ELIGIBLE">{t('fluxNode.dashboard.pnrEligible', 'Eligible')}</option>
                <option value="NOT_ELIGIBLE">{t('fluxNode.dashboard.pnrNotEligible', 'Not Eligible')}</option>
              </select>
            </div>

            {/* Maintenance Filter */}
            <div>
              <label style={{ fontSize: '11px', fontWeight: 600, color: 'var(--muted)', display: 'block', marginBottom: '4px' }}>
                <Icons.Wrench size={12} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                {t('fluxNode.dashboard.maintenance', 'Maintenance')}
              </label>
              <select
                aria-label={t('fluxNode.dashboard.maintenance', 'Maintenance')}
                value={filterMaintenance}
                onChange={(e) => setFilterMaintenance(e.target.value as any)}
                style={{
                  width: '100%',
                  padding: '6px 10px',
                  fontSize: '13px',
                  fontWeight: 600,
                  background: 'var(--card-bg)',
                  color: 'var(--text)',
                  border: `1px solid ${filterMaintenance !== 'ALL' ? 'rgba(99, 102, 241, 0.4)' : 'var(--border)'}`,
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                <option value="ALL">{t('fluxNode.dashboard.allMaintenance', 'All')}</option>
                <option value="OPEN">{t('fluxNode.dashboard.maintenanceOpen', 'Open')}</option>
                <option value="CLOSED">{t('fluxNode.dashboard.maintenanceClosed', 'Closed')}</option>
              </select>
            </div>

            {/* Rank Range */}
            <div>
              <label style={{ fontSize: '11px', fontWeight: 600, color: 'var(--muted)', display: 'block', marginBottom: '4px' }}>
                <Icons.Hash size={12} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                {t('fluxNode.dashboard.rankRange', 'Rank Range')}
              </label>
              <div style={{ display: 'flex', gap: '4px' }}>
                <input
                  type="number"
                  placeholder="Min"
                  value={filterRankMin}
                  onChange={(e) => setFilterRankMin(e.target.value)}
                  style={{
                    width: '50%',
                    padding: '6px 8px',
                    fontSize: '13px',
                    background: 'var(--card-bg)',
                    color: 'var(--text)',
                    border: `1px solid ${filterRankMin ? 'rgba(99, 102, 241, 0.4)' : 'var(--border)'}`,
                    borderRadius: '6px',
                    outline: 'none'
                  }}
                />
                <input
                  type="number"
                  placeholder="Max"
                  value={filterRankMax}
                  onChange={(e) => setFilterRankMax(e.target.value)}
                  style={{
                    width: '50%',
                    padding: '6px 8px',
                    fontSize: '13px',
                    background: 'var(--card-bg)',
                    color: 'var(--text)',
                    border: `1px solid ${filterRankMax ? 'rgba(99, 102, 241, 0.4)' : 'var(--border)'}`,
                    borderRadius: '6px',
                    outline: 'none'
                  }}
                />
              </div>
            </div>

            {/* Uptime Min (days) */}
            <div>
              <label style={{ fontSize: '11px', fontWeight: 600, color: 'var(--muted)', display: 'block', marginBottom: '4px' }}>
                <Icons.Activity size={12} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                {t('fluxNode.dashboard.uptimeMin', 'Uptime Min (days)')}
              </label>
              <input
                type="number"
                placeholder={t('fluxNode.dashboard.minDays', 'Min days')}
                value={filterUptimeMin}
                onChange={(e) => setFilterUptimeMin(e.target.value)}
                style={{
                  width: '100%',
                  padding: '6px 10px',
                  fontSize: '13px',
                  background: 'var(--card-bg)',
                  color: 'var(--text)',
                  border: `1px solid ${filterUptimeMin ? 'rgba(99, 102, 241, 0.4)' : 'var(--border)'}`,
                  borderRadius: '6px',
                  outline: 'none'
                }}
              />
            </div>

            {/* Next Payment Max (hours) */}
            <div>
              <label style={{ fontSize: '11px', fontWeight: 600, color: 'var(--muted)', display: 'block', marginBottom: '4px' }}>
                <Icons.Clock size={12} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                {t('fluxNode.dashboard.nextPaymentMax', 'Next Payment Max (h)')}
              </label>
              <input
                type="number"
                placeholder={t('fluxNode.dashboard.maxHours', 'Max hours')}
                value={filterNextPaymentMax}
                onChange={(e) => setFilterNextPaymentMax(e.target.value)}
                style={{
                  width: '100%',
                  padding: '6px 10px',
                  fontSize: '13px',
                  background: 'var(--card-bg)',
                  color: 'var(--text)',
                  border: `1px solid ${filterNextPaymentMax ? 'rgba(99, 102, 241, 0.4)' : 'var(--border)'}`,
                  borderRadius: '6px',
                  outline: 'none'
                }}
              />
            </div>

            {/* Sort By */}
            <div>
              <label style={{ fontSize: '11px', fontWeight: 600, color: 'var(--muted)', display: 'block', marginBottom: '4px' }}>
                <Icons.ArrowUpDown size={12} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                {t('fluxNode.dashboard.sortBy', 'Sort By')}
              </label>
              <div style={{ display: 'flex', gap: '4px' }}>
                <select
                  aria-label={t('fluxNode.dashboard.sortBy', 'Sort By')}
                  value={sortField}
                  onChange={(e) => setSortField(e.target.value as SortField)}
                  style={{
                    flex: 1,
                    padding: '6px 10px',
                    fontSize: '13px',
                    fontWeight: 600,
                    background: 'var(--card-bg)',
                    color: 'var(--text)',
                    border: `1px solid ${sortField !== 'default' ? 'rgba(99, 102, 241, 0.4)' : 'var(--border)'}`,
                    borderRadius: '6px',
                    cursor: 'pointer'
                  }}
                >
                  <option value="default">{t('fluxNode.dashboard.sortDefault', 'Default')}</option>
                  <option value="rank">{t('fluxNode.dashboard.sortRank', 'Rank')}</option>
                  <option value="uptime">{t('fluxNode.dashboard.sortUptime', 'Uptime')}</option>
                  <option value="nextPayment">{t('fluxNode.dashboard.sortNextPayment', 'Next Payment')}</option>
                  <option value="maintenance">{t('fluxNode.dashboard.sortMaintenance', 'Maintenance')}</option>
                  <option value="pnr">PNR %</option>
                </select>
                <button
                  onClick={() => setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')}
                  style={{
                    padding: '6px 8px',
                    background: 'var(--card-bg)',
                    color: 'var(--text)',
                    border: '1px solid var(--border)',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                  title={sortDirection === 'asc'
                    ? t('fluxNode.dashboard.ascending', 'Ascending')
                    : t('fluxNode.dashboard.descending', 'Descending')}
                >
                  {sortDirection === 'asc' ? <Icons.ChevronUp size={14} /> : <Icons.ChevronDown size={14} />}
                </button>
              </div>
            </div>
          </div>

          {/* Active filters summary */}
          {hasActiveAdvancedFilters && (
            <div style={{
              marginTop: '12px',
              paddingTop: '12px',
              borderTop: '1px solid var(--border)',
              fontSize: '11px',
              color: 'var(--muted)',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              flexWrap: 'wrap'
            }}>
              <span>{t('fluxNode.dashboard.activeFiltersCount', 'Results')}: </span>
              <span style={{ fontWeight: 700, color: 'var(--text)' }}>
                {filteredNodes.length}/{nodes.length} {t('fluxNode.dashboard.nodes', 'nodes')}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Nodes Grid/List */}
      <div style={{
        display: viewMode === 'grid' ? 'grid' : 'flex',
        gridTemplateColumns: viewMode === 'grid' ? 'repeat(auto-fill, minmax(350px, 1fr))' : undefined,
        flexDirection: viewMode === 'list' ? 'column' : undefined,
        gap: '20px'
      }}>
        {filteredNodes.map(node => {
          const nodeId = `${node.txhash}:${node.outidx}`;
          return (
            <FluxNodeCard
              key={nodeId}
              node={node}
              fluxPrice={fluxPrice}
              onRefresh={onRefresh}
              purchaseInfo={nodePurchaseTracking?.[nodeId]}
              onUpdatePurchasePrice={onUpdateNodePurchasePrice ? (price) => onUpdateNodePurchasePrice(nodeId, price) : undefined}
              onUpdateTrackingStartDate={onUpdateTrackingStartDate ? (date) => onUpdateTrackingStartDate(nodeId, date) : undefined}
              onRemoveTracking={onRemoveNodeTracking ? () => onRemoveNodeTracking(nodeId) : undefined}
              extendedStats={extendedStats}
              alias={nodeAliases?.[nodeId]}
              onUpdateAlias={onUpdateNodeAlias ? (alias) => onUpdateNodeAlias(nodeId, alias) : undefined}
              onRemoveAlias={onRemoveNodeAlias ? () => onRemoveNodeAlias(nodeId) : undefined}
              viewMode={viewMode}
              // Accordion behavior for list view - only one expanded at a time
              isListExpanded={viewMode === 'list' ? expandedListNodeId === nodeId : undefined}
              onToggleListExpand={viewMode === 'list' ? () => setExpandedListNodeId(expandedListNodeId === nodeId ? null : nodeId) : undefined}
              // Datavex hosted IPs for badge display
              datavexHostedIps={datavexHostedIps}
              // Datavex expiration info
              datavexExpirations={datavexExpirations}
            />
          );
        })}
      </div>

      {filteredNodes.length === 0 && (
        <div style={{ textAlign: 'center', padding: '40px', color: 'var(--muted)' }}>
          {t('fluxNode.dashboard.noNodesMatchFilter')}
        </div>
      )}

      {/* Hosting Promo Popup */}
      <HostingPromoPopup
        datavexStats={datavexStats}
        wallets={wallets}
        nodesSummary={{
          total: nodes.length,
          cumulus: nodesByTier.CUMULUS,
          nimbus: nodesByTier.NIMBUS,
          stratus: nodesByTier.STRATUS
        }}
      />
    </div>
  );
}
